import { useEffect, useState, useMemo } from 'react';
import { AxiosError } from 'axios';
import {
  NudosAddressSearchBar,
  NudosButton,
  NudosCityDropdown,
  NudosPhoneInput,
  NudosTextInput
} from '../../../../../../components/NudosComponents';
import { ICountryDetail } from '../../../../../../types/countries';
import {
  IUpdateLogisticServiceAddress,
  IlogisticServiceDetails,
  LOCATION_PLACE_TYPE,
  TEditField
} from '../../../../../../types/services';
import { countriesList } from '../../../../../../services/countriesList';
import { updateLogisticServiceAddress } from '../../../../../../services/services';
import { IcityDetails } from '../../../../../../types/cities.types';
import ConfirmAddressChangeModal from '../ConfirmAddressChangeModal/ConfirmAddressChangeModal';
import { IaddressFromGoogleMaps } from '../../../../../../types/global';
import { getAddressErrorText } from '../../../../../../utils/formValidations';
import { displayErrorNotification, displaySuccessNotification } from '../../../../../../utils/displayNudosStandardNotifications';
import './LogisticServiceEmployeeOfficeLocation.scss';

const LogisticServiceEmployeeOfficeLocation = ({
  location,
  logisticServiceData,
  enableEditAddress,
  updateDataCallback,
  updateWithoutLoader
}: {
  location: 'origin' | 'destination';
  logisticServiceData: IlogisticServiceDetails;
  enableEditAddress: boolean;
  updateDataCallback: () => Promise<void>;
  updateWithoutLoader: () => Promise<void>;
}) => {
  const [formCity, setFormCity] = useState<IcityDetails>();
  const [countries, setCountries] = useState<ICountryDetail[]>();
  const [formAddress, setFormAddress] = useState<IaddressFromGoogleMaps>();
  const [formZipCode, setFormZipCode] = useState<string>();
  const [formIdDocument, setFormIdDocument] = useState<string>();
  const [formPhoneNumber, setFormPhoneNumber] = useState<string>();
  const [countriesLoader, setCountriesLoader] = useState<boolean>(false);
  const [updateDataLoader, setUpdateDataLoader] = useState<boolean>(false);
  const [valuesHadChanged, setValuesHadChanged] = useState<boolean>(false);
  const [formPhoneCountry, setFormPhoneCountry] = useState<ICountryDetail>();
  const [formAdditionalReferences, setFormAdditionalReferences] = useState<string>();
  const [openConfirmEditFieldsModal, setOpenConfirmEditFieldsModal] = useState<boolean>(false);
  const [editedFields, setEditFields] = useState<TEditField[]>([]);

  const { destinationLocation, deliveryInfo, originLocation, collectionInfo, id } = logisticServiceData;

  const locationIsOrigin = useMemo(() => location === 'origin', [location]);

  const originDestinationData = useMemo(
    () => (locationIsOrigin ? originLocation : destinationLocation),
    [locationIsOrigin, originLocation, destinationLocation]
  );

  const collectionDeliveryData = useMemo(
    () => (locationIsOrigin ? collectionInfo : deliveryInfo),
    [locationIsOrigin, collectionInfo, deliveryInfo]
  );

  const phoneNumber = useMemo(
    () => (locationIsOrigin ? collectionDeliveryData?.delivererPhone : collectionDeliveryData?.receiverPhone),
    [locationIsOrigin, collectionDeliveryData]
  );

  const name = useMemo(
    () => (locationIsOrigin ? collectionDeliveryData?.delivererName : collectionDeliveryData?.receiverName),
    [locationIsOrigin, collectionDeliveryData]
  );

  const additionalReferences = useMemo(() => originDestinationData?.additionalReferences, [originDestinationData]);

  const employeeDestination = useMemo(
    () => originDestinationData?.place === LOCATION_PLACE_TYPE?.USER,
    [originDestinationData]
  );

  const type = useMemo(
    () => (employeeDestination ? LOCATION_PLACE_TYPE?.USER : LOCATION_PLACE_TYPE?.OFFICE),
    [employeeDestination]
  );

  const idDocument = useMemo(
    () => (locationIsOrigin ? collectionDeliveryData?.delivererPersonalId : collectionDeliveryData?.receiverPersonalId),
    [locationIsOrigin, collectionDeliveryData]
  );

  const phoneCountry = useMemo(
    () =>
      locationIsOrigin ? collectionDeliveryData?.delivererCountryPhone : collectionDeliveryData?.receiverCountryPhone,
    [locationIsOrigin, collectionDeliveryData]
  );

  const noDataText = 'Sin datos';
  const displayEmployeeInputs = useMemo(() => type === LOCATION_PLACE_TYPE?.USER, [type]);
  const displayOfficeInputs = useMemo(() => type === LOCATION_PLACE_TYPE?.OFFICE, [type]);
  const disabledInputs = useMemo(() => updateDataLoader || !enableEditAddress, [updateDataLoader, enableEditAddress]);

  const currentCountry = useMemo(
    () => countries?.find(country => country?.name === originDestinationData?.country?.name),
    [countries, originDestinationData?.country?.name]
  );

  const originDestinationText = useMemo(() => (locationIsOrigin ? 'origen' : 'destino'), [locationIsOrigin]);
  const cityId = useMemo(
    () => (locationIsOrigin ? originLocation?.cityId : destinationLocation?.cityId),
    [locationIsOrigin, originLocation, destinationLocation]
  );
  const employeeData = useMemo(
    () => (locationIsOrigin ? collectionInfo?.user : deliveryInfo?.user),
    [locationIsOrigin, collectionInfo, deliveryInfo]
  );
  const employeeName = useMemo(
    () => (locationIsOrigin ? collectionInfo?.delivererName : deliveryInfo?.receiverName),
    [locationIsOrigin, collectionInfo, deliveryInfo]
  );
  const sameCity = useMemo(
    () => originLocation?.city === destinationLocation?.city,
    [originLocation?.city, destinationLocation?.city]
  );

  const cityDetails = useMemo(
    () => ({
      id: cityId,
      name: originDestinationData?.city,
      country: currentCountry
    }),
    [cityId, originDestinationData?.city, currentCountry]
  );

  const addressErrorText = getAddressErrorText(
    originDestinationData?.country?.code,
    originDestinationData?.country?.name,
    formAddress?.country || undefined
  );

  const addressIssues = logisticServiceData?.addressIssue;
  const showOriginEditedFields = addressIssues?.origin?.addressEdited && !addressIssues?.origin?.viewedChanges;
  const showDestinationEditedFields =
    addressIssues?.destination?.addressEdited && !addressIssues?.destination?.viewedChanges;
  const allOriginEdits = addressIssues?.origin?.editHistory;
  const originEditedFields =
    Array.isArray(allOriginEdits) && allOriginEdits.length > 0 ? allOriginEdits[allOriginEdits.length - 1] : undefined;

  const allDestinationEdits = addressIssues?.destination?.editHistory;
  const destinationEditedFields =
    Array.isArray(allDestinationEdits) && allDestinationEdits.length > 0
      ? allDestinationEdits[allDestinationEdits.length - 1]
      : undefined;

  const isFieldEditedByDash = (fieldName: string): boolean => {
    const editedFieldsObj = locationIsOrigin ? originEditedFields : destinationEditedFields;
    const showEditedFields = locationIsOrigin ? showOriginEditedFields : showDestinationEditedFields;

    if (!showEditedFields || !editedFieldsObj || editedFieldsObj.platform !== 'DASH') return false;
    return Array.isArray(editedFieldsObj.editedFields) && editedFieldsObj.editedFields.includes(fieldName);
  };

  const setInitialData = () => {
    if (countriesLoader) return;
    setFormZipCode(originDestinationData?.zipCode || '');
    setFormPhoneNumber(phoneNumber || '');
    setFormPhoneCountry(phoneCountry || undefined);
    setFormAdditionalReferences(additionalReferences || '');
    setFormIdDocument(idDocument || '');
    setFormCity(cityDetails || undefined);
    setFormAddress(undefined);
  };

  const getLocationName = () => {
    if (originDestinationData?.place === LOCATION_PLACE_TYPE?.OFFICE) return 'Oficinas';
    if (originDestinationData?.place === LOCATION_PLACE_TYPE?.OTHER) return 'Otra dirección';
    return noDataText;
  };

  const newOfficeLocationData: IUpdateLogisticServiceAddress = {
    address: {
      address: formAddress?.address?.trim() || '',
      additionalReferences: formAdditionalReferences?.trim() || '',
      contactInformation: {
        name: employeeName?.trim() || '',
        phone: formPhoneNumber?.trim(),
        phoneCountryCode: formPhoneCountry?.code,
        personalId: formIdDocument?.trim() || ''
      },
      city: formCity?.name?.trim() || '',
      cityId: formCity?.id || undefined,
      zipCode: formZipCode?.trim() || '',
      country: currentCountry?.code || '',
      countryId: currentCountry?.id
    }
  };

  const newEmployeeLocationData: IUpdateLogisticServiceAddress = {
    firstName: employeeData?.firstName?.trim(),
    lastName: employeeData?.lastName?.trim(),
    personalId: formIdDocument?.trim() || '',
    countryId: currentCountry?.id || undefined,
    countryPhoneId: formPhoneCountry?.id || undefined, // This should be the countryId of the organization MS since the data is passed to the organization MS to update the employee information
    phone: formPhoneNumber?.trim() || '',
    address: {
      address: formAddress?.address?.trim() || '',
      additionalReferences: formAdditionalReferences?.trim() || '',
      city: formCity?.name?.trim() || '',
      cityId: formCity?.id || undefined,
      zipCode: formZipCode?.trim() || '',
      country: currentCountry?.code || '',
      contactInformation: {
        name: employeeName?.trim() || '',
        phone: formPhoneNumber?.trim() || '',
        phoneCountryCode: formPhoneCountry?.code || undefined,
        personalId: formIdDocument?.trim() || ''
      }
    }
  };

  const newDestinationData = displayEmployeeInputs ? newEmployeeLocationData : newOfficeLocationData;

  const receiveUpdatedFields = (fieldName: TEditField) => {
    const filterFields = editedFields.find(field => fieldName === field);
    if (!filterFields) {
      editedFields.push(fieldName);
      setEditFields([...editedFields]);
    }
  };

  const updateLocationData = async () => {
    if (!id) return;
    setUpdateDataLoader(true);
    try {
      await updateLogisticServiceAddress(id, location, newDestinationData);
      setOpenConfirmEditFieldsModal(false);
      displaySuccessNotification({
        customJSXMessage: <>{`Actualizaste la información de ${originDestinationText} del envío con éxito`}</>
      });
      await updateDataCallback();
      await updateWithoutLoader();
    } catch (err) {
      const error = err as AxiosError;
      displayErrorNotification({ customJSXMessage: <>{error?.response?.data?.message}</> });
    } finally {
      setUpdateDataLoader(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchCountries = async () => {
      setCountriesLoader(true);
      try {
        const countries = await countriesList();
        if (isMounted) setCountries(countries);
      } catch (error) {
        if (isMounted) displayErrorNotification();
      } finally {
        if (isMounted) setCountriesLoader(false);
      }
    };
    fetchCountries();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) setInitialData();
    return () => {
      isMounted = false;
    };
  }, [
    originDestinationData,
    phoneNumber,
    phoneCountry,
    additionalReferences,
    idDocument,
    cityDetails,
    countriesLoader
  ]);

  return (
    <div className="LogisticServiceEmployeeOfficeLocation">
      {openConfirmEditFieldsModal && editedFields && editedFields.length > 0 && (
        <ConfirmAddressChangeModal
          logisticServiceData={logisticServiceData}
          submitModalCallback={updateLocationData}
          closeModalCallback={() => setOpenConfirmEditFieldsModal(false)}
          location={location}
          loading={updateDataLoader}
          selectedReportingOptions={[]}
          typeConfirmChanges={openConfirmEditFieldsModal}
          editedFields={editedFields}
          valueOfEditedFields={{
            address: formAddress?.address || '',
            zipCode: formZipCode,
            phone: `+${formPhoneCountry?.phoneCode} ${formPhoneNumber}`,
            city: formCity?.name || '',
            additionalReferences: formAdditionalReferences,
            personalId: formIdDocument
          }}
        />
      )}
      <div className="LogisticServiceEmployeeOffice">
        {displayEmployeeInputs && (
          <NudosTextInput
            label="Nombre y apellido"
            isFilled={true}
            placeholder={employeeName || noDataText}
            defaultValue={employeeName || noDataText}
            isDeactivated
            componentSize="small"
          />
        )}
        {displayEmployeeInputs && (
          <NudosTextInput
            label="Correo"
            isFilled={true}
            placeholder={collectionDeliveryData?.user?.email || noDataText}
            defaultValue={collectionDeliveryData?.user?.email || noDataText}
            isDeactivated
            componentSize="medium"
          />
        )}
        {displayOfficeInputs && (
          <NudosTextInput
            label="Ubicacion"
            isFilled={true}
            placeholder={getLocationName()}
            defaultValue={getLocationName()}
            isDeactivated
            componentSize="small"
          />
        )}
        <NudosPhoneInput
          label="Número de contacto"
          isDisabled={disabledInputs}
          placeholder={formPhoneNumber || noDataText}
          countriesList={countries || []}
          componentSize="medium"
          defaultPhoneNumber={formPhoneNumber}
          defaultCountryData={formPhoneCountry}
          handlePhoneChange={setFormPhoneNumber}
          handleCountryPhoneChange={setFormPhoneCountry}
          simpleChangeOfValues={() => {
            setValuesHadChanged(true);
            receiveUpdatedFields('phone');
          }}
          styleLabel={isFieldEditedByDash('phone') ? 'dashEditedPhone' : ''}
        />
        <NudosTextInput
          label="Pais"
          isFilled={true}
          placeholder={originDestinationData?.country?.name || noDataText}
          defaultValue={originDestinationData?.country?.name || noDataText}
          componentSize="small"
          isDeactivated
        />
        <NudosCityDropdown
          countryId={currentCountry?.id}
          isDeactivated={disabledInputs || sameCity}
          componentSize="small"
          currentValue={formCity}
          updateCurrentValueCallback={value => setFormCity(value)}
          handleBlur={() => {
            setValuesHadChanged(true);
            receiveUpdatedFields('city');
          }}
          sortAlphabetically
        />
        <NudosAddressSearchBar
          label="Dirección"
          errorText={addressErrorText}
          isDisabled={disabledInputs}
          regionBias={originDestinationData?.country?.code}
          componentSize="large"
          handleAddressSelection={value => {
            setFormAddress(value);
            setValuesHadChanged(true);
            receiveUpdatedFields('address');
          }}
          didntFindAddressTooltipTopPosition={56 + (addressErrorText ? 10 : 0)}
          didntFindAddressTooltipRightPosition={0}
          defaultValueAddressName={formAddress?.address || originDestinationData?.address || noDataText}
          styleLabel={isFieldEditedByDash('address') ? 'dashEditedAddress' : ''}
        />
        <NudosTextInput
          label="Indicaciones"
          isFilled={true}
          placeholder={formAdditionalReferences || noDataText}
          defaultValue={formAdditionalReferences}
          isDeactivated={disabledInputs}
          handleChange={value => {
            setFormAdditionalReferences(value);
            setValuesHadChanged(true);
            receiveUpdatedFields('additionalReferences');
          }}
          componentSize="medium"
          customClassName={isFieldEditedByDash('additionalReferences') ? 'dashEditedTextInput' : ''}
        />
        <NudosTextInput
          label="CP*"
          isFilled={true}
          placeholder={formZipCode || noDataText}
          defaultValue={formZipCode}
          handleChange={value => {
            setFormZipCode(value);
            setValuesHadChanged(true);
            receiveUpdatedFields('zipCode');
          }}
          isDeactivated={disabledInputs || sameCity}
          componentSize="extraSmall"
          customClassName={isFieldEditedByDash('zipCode') ? 'dashEditedTextInput' : ''}
        />
        {displayOfficeInputs && (
          <NudosTextInput
            label={locationIsOrigin ? 'Quién entrega' : 'Quién recibe'}
            isFilled={true}
            placeholder={name || noDataText}
            defaultValue={name || noDataText}
            componentSize="small"
            isDeactivated
            customClassName={isFieldEditedByDash('name') ? 'dashEditedTextInput' : ''}
          />
        )}
        <NudosTextInput
          label="Documento de identidad"
          isFilled={true}
          placeholder={formIdDocument || noDataText}
          defaultValue={formIdDocument}
          handleChange={value => {
            setFormIdDocument(value);
            setValuesHadChanged(true);
            receiveUpdatedFields('personalId');
          }}
          componentSize="small"
          isDeactivated={disabledInputs}
          customClassName={isFieldEditedByDash('personalId') ? 'dashEditedTextInput' : ''}
        />
      </div>
      {enableEditAddress && (
        <div className="buttonsContainer">
          <NudosButton
            buttonText="Guardar"
            handleClick={() => setOpenConfirmEditFieldsModal(true)}
            componentSize="extraSmall"
            customClassName="saveButton"
            isButtonLoading={updateDataLoader}
            isButtonDisabled={!valuesHadChanged}
          />
          <NudosButton
            buttonText="Cancelar"
            handleClick={updateWithoutLoader}
            componentSize="extraSmall"
            customClassName="saveButton"
            isButtonLoading={updateDataLoader}
            colorPalette="secondary"
          />
        </div>
      )}
    </div>
  );
};

export default LogisticServiceEmployeeOfficeLocation;
